import type { ProjectStorage } from './ProjectStorage';
import type { StorageConfig } from './storageConfig';
import { getSession } from '../auth/session';

export class StorageError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

export class RemoteProjectStorage<TLevel> implements ProjectStorage<TLevel> {
  private base: string;
  private projectId: string;

  constructor(config: StorageConfig) {
    this.base = config.apiBaseUrl;
    this.projectId = config.projectId;
  }

  private url(path: string): string {
    return `${this.base}/projects/${this.projectId}/${path}`;
  }

  /** Session is read per request so a fresh login applies without a reload. */
  private headers(extra?: Record<string, string>): Record<string, string> {
    const session = getSession();
    return {
      ...(session ? { Authorization: `Bearer ${session}` } : {}),
      ...extra,
    };
  }

  private async throwResponseError(res: Response, context: string): Promise<never> {
    const body = await res.json().catch(() => ({})) as { code?: string; message?: string };
    if (res.status === 401) {
      throw new StorageError('UNAUTHORIZED', body.message ?? 'GitHub sign-in required');
    }
    throw new StorageError(body.code ?? 'UNKNOWN', body.message ?? `${context} failed: ${res.status}`);
  }

  private async getJson<T>(path: string): Promise<T> {
    let res: Response;
    try {
      res = await fetch(this.url(path), { cache: 'no-store', headers: this.headers() });
    } catch (e) {
      throw new StorageError('NETWORK_ERROR', `Network error on GET ${path}: ${String(e)}`);
    }
    if (!res.ok) await this.throwResponseError(res, `GET ${path}`);
    return res.json() as Promise<T>;
  }

  private async putJson(path: string, body: unknown): Promise<void> {
    let res: Response;
    try {
      res = await fetch(this.url(path), {
        method: 'PUT',
        headers: this.headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
    } catch (e) {
      throw new StorageError('NETWORK_ERROR', `Network error on PUT ${path}: ${String(e)}`);
    }
    if (!res.ok) await this.throwResponseError(res, `PUT ${path}`);
  }

  private async deleteReq(path: string): Promise<void> {
    let res: Response;
    try {
      res = await fetch(this.url(path), { method: 'DELETE', headers: this.headers() });
    } catch (e) {
      throw new StorageError('NETWORK_ERROR', `Network error on DELETE ${path}: ${String(e)}`);
    }
    if (!res.ok) await this.throwResponseError(res, `DELETE ${path}`);
  }

  async loadLineup(): Promise<string[]> {
    const data = await this.getJson<{ levels: string[] }>('lineup');
    return Array.isArray(data.levels) ? data.levels : [];
  }

  async saveLineup(levelIds: string[]): Promise<void> {
    return this.putJson('lineup', { levels: levelIds });
  }

  async listLevels(): Promise<string[]> {
    const data = await this.getJson<{ levels: string[] }>('levels');
    return Array.isArray(data.levels) ? data.levels : [];
  }

  async loadLevel(id: string): Promise<TLevel> {
    return this.getJson<TLevel>(`levels/${id}`);
  }

  async saveLevel(id: string, level: TLevel): Promise<void> {
    return this.putJson(`levels/${id}`, level);
  }

  async deleteLevel(id: string): Promise<void> {
    return this.deleteReq(`levels/${id}`);
  }

  async listImages(): Promise<string[]> {
    const data = await this.getJson<{ images: string[] }>('images');
    return Array.isArray(data.images) ? data.images : [];
  }

  getImageUrl(path: string): string {
    const url = `${this.base}/projects/${this.projectId}/images/${path.replace(/^images\//, '')}`;
    // <img> tags cannot send the Authorization header — pass the session as a
    // query parameter instead (the backend accepts it for image GETs only).
    const session = getSession();
    return session ? `${url}?session=${encodeURIComponent(session)}` : url;
  }
}
